/**
 * YouTube Data API Integration
 * Documentation: https://developers.google.com/youtube/v3/docs
 */

import axios from 'axios';
import config from '../config';

const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3';

class YouTubeService {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Fetch videos from a YouTube channel
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of video objects
   */
  async getChannelVideos(options = {}) {
    const {
      maxResults = config.youtube.maxResults,
      playlistId = null,
      orderBy = 'date',
    } = options;

    const cacheKey = `channel_videos_${maxResults}_${playlistId}_${orderBy}`;

    // Check cache
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey).data;
    }

    try {
      let videoIds = [];

      if (playlistId) {
        // Fetch from specific playlist
        videoIds = await this.getPlaylistVideos(playlistId, maxResults);
      } else {
        // Fetch from channel's uploads
        const uploadsPlaylistId = await this.getUploadsPlaylistId();
        videoIds = await this.getPlaylistVideos(uploadsPlaylistId, maxResults);
      }

      // Get video details
      const videos = await this.getVideoDetails(videoIds);

      // Cache the results
      this.setCacheData(cacheKey, videos);

      return videos;
    } catch (error) {
      console.error('YouTube API Error:', error.response?.data || error.message);
      return this.getFallbackData(cacheKey);
    }
  }

  /**
   * Get the uploads playlist ID for the channel
   */
  async getUploadsPlaylistId() {
    const cacheKey = 'uploads_playlist_id';

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey).data;
    }

    const response = await axios.get(`${YOUTUBE_API_BASE}/channels`, {
      params: {
        part: 'contentDetails',
        id: config.youtube.channelId,
        key: config.youtube.apiKey,
      },
    });

    const uploadsPlaylistId = response.data.items[0]?.contentDetails?.relatedPlaylists?.uploads;

    if (!uploadsPlaylistId) {
      throw new Error('Could not find uploads playlist');
    }

    this.setCacheData(cacheKey, uploadsPlaylistId);
    return uploadsPlaylistId;
  }

  /**
   * Get videos from a playlist
   */
  async getPlaylistVideos(playlistId, maxResults) {
    const response = await axios.get(`${YOUTUBE_API_BASE}/playlistItems`, {
      params: {
        part: 'contentDetails',
        playlistId,
        maxResults,
        key: config.youtube.apiKey,
      },
    });

    return response.data.items.map(item => item.contentDetails.videoId);
  }

  /**
   * Parse ISO 8601 duration to seconds
   * Example: PT1H2M10S -> 3730 seconds
   */
  parseDuration(isoDuration) {
    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;

    const hours = parseInt(match[1] || 0);
    const minutes = parseInt(match[2] || 0);
    const seconds = parseInt(match[3] || 0);

    return hours * 3600 + minutes * 60 + seconds;
  }

  /**
   * Get detailed information about videos
   */
  async getVideoDetails(videoIds) {
    if (!videoIds || videoIds.length === 0) {
      return [];
    }

    const response = await axios.get(`${YOUTUBE_API_BASE}/videos`, {
      params: {
        part: 'snippet,contentDetails,statistics',
        id: videoIds.join(','),
        key: config.youtube.apiKey,
      },
    });

    return response.data.items.map(video => ({
      id: video.id,
      platform: 'youtube',
      title: video.snippet.title,
      description: video.snippet.description,
      thumbnail: video.snippet.thumbnails.high.url,
      embedUrl: `https://www.youtube.com/embed/${video.id}`,
      url: `https://www.youtube.com/watch?v=${video.id}`,
      publishedAt: video.snippet.publishedAt,
      tags: video.snippet.tags || [],
      duration: video.contentDetails.duration,
      durationSeconds: this.parseDuration(video.contentDetails.duration),
      viewCount: parseInt(video.statistics.viewCount || 0),
    }));
  }

  /**
   * Filter videos by keywords or tags
   */
  filterByKeywords(videos, keywords) {
    if (!keywords || keywords.length === 0) {
      return videos;
    }

    return videos.filter(video => {
      const searchText = `${video.title} ${video.description} ${video.tags.join(' ')}`.toLowerCase();
      return keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
    });
  }

  /**
   * Cache management
   */
  isCacheValid(key) {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < config.cache.youtube;
  }

  setCacheData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  getFallbackData(key) {
    const cached = this.cache.get(key);
    return cached ? cached.data : [];
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

export default new YouTubeService();
